/**
 * @package Coursel
 *
 * Entry point for Javascript package
*/

type ElementPosition = {
  start: number,
  end: number,
};

interface CarouselState {
  parent: Element;
  wrapper: Element;
  elements: Array<HTMLElement>;
  elementCount: number;
  activeElement: number;
  classPrefix: string;
  buttons: Array<HTMLButtonElement | undefined>;
  elePositions: Array<ElementPosition>;
  indicatorWrapper?: HTMLElement;
  indicators: Array<HTMLElement>;
}

class SoulCarousel {
  state:CarouselState;

  constructor(
    container: Element,
    itemSelector: string,
    prefix: string,
  ) {
    // Store all required values in state object
    this.state = {
      parent: container,
      wrapper: container.children[0],
      elements: Array.from(container.querySelectorAll(itemSelector)),
      classPrefix: prefix,
      elementCount: 0,
      activeElement: 0,
      buttons: [],
      elePositions: [],
      indicatorWrapper: undefined,
      indicators: [],
    };

    // Set ammount of elements
    this.state.elementCount = this.state.elements.length;
    // Add class to wrapper
    this.state.wrapper.classList.add('sCarousel__wrapper');

    // Loop over carousel items
    this.state.elements.forEach((ele, index) => {
      // Add class
      ele.classList.add('sCarousel__item');
      ele.dataset.index = index.toString();
      // Store postion values
      this.state.elePositions[index] = {
        start: ele.offsetLeft,
        end: ele.getBoundingClientRect().width,
      };
    });

    // Add the indicators
    this.createIndicators();
    // Add switch buttons
    this.createCarouselInputs();
    // create intersection observer
    this.createObserver();
    // Add event listeners
    this.addEventListeners();
  }

  createIndicators() {
    const { elementCount, parent } = this.state;

    // Create wrapper for indicators
    const indicatorWrapper = document.createElement('div');
    indicatorWrapper.classList.add(`${this.state.classPrefix}indicatorWrapper`);
    // Create required indicators and add them to the wrapper
    for (let i = 0; i < elementCount; i += 1) {
      const indicator = document.createElement('span');
      indicator.classList.add(`${this.state.classPrefix}indicator`);
      this.state.indicators[i] = indicator;
      if (i === 0) {
        indicator.dataset.active = 'true';
      }
      indicatorWrapper.appendChild(indicator);
    }

    this.state.indicatorWrapper = indicatorWrapper;

    // Add indicators to the parent
    parent.appendChild(indicatorWrapper);
  }

  createCarouselInputs() {
    const buttons:Array<HTMLButtonElement> = [];

    for (let i = 0; i < 2; i += 1) {
      const btn: HTMLButtonElement = document.createElement('button');
      btn.type = 'button';
      btn.classList.add(`${this.state.classPrefix}button`);
      this.state.indicatorWrapper?.appendChild(btn);
      buttons[i] = btn;
    }

    this.state.buttons = buttons;
  }

  createObserver() {
    const { wrapper, elements } = this.state;
    const options = {
      root: wrapper,
      rootMargin: '0px',
      threshold: 0.5,
    };

    const callback = (entries: any) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting) {
          const { index } = entry.target.dataset;
          this.state.indicators[index].dataset.active = 'true';
        } else {
          const { index } = entry.target.dataset;
          this.state.indicators[index].dataset.active = 'false';
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);

    elements.forEach((ele) => {
      // @ts-ignore
      observer.observe(ele);
    });
  }

  addEventListeners() {
    const {
      buttons,
      wrapper,
      elePositions,
      indicators,
    } = this.state;

    buttons.forEach((btn, index) => {
      btn?.addEventListener('click', () => {
        switch (index) {
          case 0: {
            this.handleSwitch(false);
            break;
          }
          default: {
            this.handleSwitch(true);
            break;
          }
        }
      });
    });

    wrapper.addEventListener('scroll', () => {
      const scroll = wrapper.scrollLeft;
      elePositions.forEach((pos, index) => {
        if (scroll > pos.start && scroll < pos.end) {
          this.state.activeElement = index;
        }
      });
    }, { passive: true });

    indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => {
        this.handleSwitch(true, index);
      });
    });
  }

  handleSwitch(switcher:Boolean, target:Boolean | number = false) {
    const { elementCount, elements, wrapper } = this.state;
    let newIndex = this.state.activeElement;

    if (switcher) {
      newIndex += 1;
      if (newIndex > elementCount - 1) {
        newIndex = 0;
      }
    } else {
      newIndex -= 1;
      if (newIndex < 0) {
        newIndex = elementCount - 1;
      }
    }

    if (target !== false) {
      // @ts-ignore
      newIndex = target;
    }

    this.state.activeElement = newIndex;
    this.state.indicators.forEach((indicator) => {
      indicator.dataset.active = 'false';
    });
    this.state.indicators[newIndex].dataset.active = 'true';

    const newIndexPosition = elements[newIndex].offsetLeft;
    wrapper.scrollTo({
      top: 0,
      left: newIndexPosition,
      behavior: 'smooth',
    });
  }
}

export default SoulCarousel;

// @ts-ignore
window.carousel = SoulCarousel;
